import { forwardRef, useMemo } from 'react';
import { Text, Stack, Link, useTheme, Icon } from '@fluentui/react';
import { styles } from './CardItem.styles';
import { useGetStyles } from '../../useGetStyles';
import { DataverseLogo } from '../Logos/DataverseLogo';
import './CardItem.scss';
import { GridContainer } from './components/GridContainer';
import { GridRow } from './components/GridRow';
import { IntegrationTypeGeneric, IsDataverseIntegration, IsMicrosoftEntraIdIntegration, IsTeamPlannerIntegration } from '../../IntegrationTypeGeneric';
import { MicrosoftEntraIdLogo } from '../Logos/MicrosoftEntraIdLogo';
import { getEventIconFromEnum } from '../../getEventIconFromEnum';
import { getEventTextFromEnum } from '../../getEventTextFromEnum';
import { TeamPlannerLogo } from '@pum-spark/projectum-logos-react';
import { IntegrationType } from '../../../../../../api/generated/data-contracts';
import { GetWithScheme } from '../../../../../../helpers/trimUrl';

export const CardItem = forwardRef<
    HTMLDivElement,
    { integration: IntegrationTypeGeneric<any, any>; setEditableIntegration: (integration: IntegrationTypeGeneric<any, any>) => void }
>(function CardItem({ integration, setEditableIntegration }, ref) {
    const theme = useTheme();
    const classes = useGetStyles(styles);

    const configRender = useMemo(() => {
        if (IsDataverseIntegration(integration)) {
            return (
                <>
                    <GridRow
                        left="Url"
                        right={
                            <Text nowrap title={integration.configuration.dataverseUrl}>
                                <Link
                                    target="_blank"
                                    rel="noreferrer"
                                    href={integration.configuration.dataverseUrl}
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                >
                                    {GetWithScheme(integration.configuration.dataverseUrl)}
                                </Link>
                            </Text>
                        }
                    />
                    <GridRow
                        left={'Enabled'}
                        right={
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                {integration.subscribedEvents?.map(event => (
                                    <Stack key={event} horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                                        <Icon iconName={getEventIconFromEnum(event)} title={getEventTextFromEnum(event)} />
                                    </Stack>
                                ))}
                            </Stack>
                        }
                    />
                </>
            );
        }
        if (IsMicrosoftEntraIdIntegration(integration)) {
            return (
                <>
                    <GridRow left={'Group Name'} right={integration.configuration.groupName} />
                    <GridRow
                        left={'Enabled'}
                        right={
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                {integration.configuration.enabled && (
                                    <Stack key={'entraSyncEnabled'} horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                                        <Icon iconName={'CloudDownload'} title={'In'} />
                                    </Stack>
                                )}
                                {integration.configuration.syncUsersToDataverse && (
                                    <Stack key={'dataverseResourceSyncEnabled'} horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                                        <Icon iconName={'CloudUpload'} title={'Out'} />
                                    </Stack>
                                )}
                                {!integration.configuration.enabled && !integration.configuration.syncUsersToDataverse && <Text>None</Text>}
                            </Stack>
                        }
                    />
                </>
            );
        }
        if (IsTeamPlannerIntegration(integration)) {
            return (
                <>
                    <GridRow
                        left="Url"
                        right={
                            <Text nowrap title={integration.configuration.url}>
                                <Link
                                    target="_blank"
                                    rel="noreferrer"
                                    href={integration.configuration.url}
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                >
                                    {integration.configuration.url}
                                </Link>
                            </Text>
                        }
                    />
                    <GridRow
                        left={'Enabled'}
                        right={
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                {integration.subscribedEvents?.length === 0 ? (
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                                        <Text>None</Text>
                                    </Stack>
                                ) : (
                                    integration.subscribedEvents?.map(event => (
                                        <Stack key={event} horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                                            <Icon iconName={getEventIconFromEnum(event)} title={getEventTextFromEnum(event)} />
                                        </Stack>
                                    ))
                                )}
                            </Stack>
                        }
                    />
                </>
            );
        }

        return <div>Unknown integration type</div>;
    }, [integration]);

    return (
        <div className={classes.root} ref={ref} onClick={() => setEditableIntegration(integration)} style={{ cursor: 'pointer' }}>
            <Stack verticalAlign="start">
                <Stack
                    horizontal
                    tokens={{ childrenGap: theme.spacing.m }}
                    verticalAlign="center"
                    styles={{ root: { paddingBottom: theme.spacing.m, width: '100%' } }}
                >
                    <Stack>
                        <CardLogo type={integration.type} />
                    </Stack>
                    {/* <Stack styles={{root: {display: "inline-grid", flexGrow: 1}}}> */}
                    <Stack styles={{ root: { display: 'inline-grid', flexGrow: 1 } }}>
                        <Text nowrap={false} className={classes.title}>
                            {integration.name ?? integration.configuration.dataverseUrl}
                        </Text>
                    </Stack>
                </Stack>
                <div className={classes.divider} />
                <GridContainer>
                    <GridRow left={'Type'} right={getIntegrationTypeName(integration.type)} />
                    {configRender}
                </GridContainer>
            </Stack>
        </div>
    );
});

const getIntegrationTypeName = (type: IntegrationType) => {
    switch (type) {
        // case IntegrationType.Dataverse:
        case 'Dataverse':
            return 'Dataverse';
        case 'MicrosoftEntraId':
            return 'MicrosoftEntraId';
        case 'TeamPlanner':
            return 'Team Planner';
        default:
            return 'Unknown type';
    }
};

const CardLogo = ({ type }: { type: IntegrationType }) => {
    if (type === 'Dataverse') {
        return <DataverseLogo height={'35px'} width={'35px'} />;
    }

    if (type === 'MicrosoftEntraId') {
        return <MicrosoftEntraIdLogo height={'35px'} width={'35px'} />;
    }

    if (type === 'TeamPlanner') {
        return <TeamPlannerLogo height={'35px'} width={'35px'} />;
    }

    return <div>NO LOGO</div>;
};

export type DataverseConfiguration = {
    url: string;
    enabled: boolean;
};

export type MicrosoftEntraIdConfiguration = {
    enabled: boolean;
    groupName: string;
    includeManager: boolean;
    syncUsersToDataverse: boolean;
};
